import { CommonModule } from '@angular/common'
import { HttpRequest, HttpResponse } from '@angular/common/http'
import { ChangeDetectionStrategy, Component, ElementRef, HostBinding, inject, Input, QueryList, Renderer2, ViewChild, ViewChildren, ViewEncapsulation } from '@angular/core'
import { NgElement, WithProperties } from '@angular/elements'
import { registerCustomElementFactory } from '../../custom-elements/register-custom-element-factory'
import { AsyncActionState } from '../async-action-state'
import { DownloadEventData, DownloadFailedEventData, DownloadCompletedEventData, DownloadEvent } from './download-event'
import { DownloadContentDisposition, DownloadOptions } from './download-options'
import { filter, map } from 'rxjs/operators'
import { ActionComponent } from '../action/action.component'

/**
 * @public
 */
export type DownloadElement = NgElement & WithProperties<DownloadComponent>

/**
 * Component that can be used to securely download a file from an HTTP API
 *
 * @public
 */
@Component({
  selector: 'gpei-download',
  templateUrl: './download.component.html',
  styleUrls: [ './download.component.sass' ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.ShadowDom,
  standalone: true,
  imports: [
    CommonModule
  ]
})
export class DownloadComponent extends ActionComponent {

  /**
   * The name of the file downloaded
   */
  @Input()
  @HostBinding()
  fileName = 'download'

  /**
   * State that determines if if the options menu is visible, a.k.a. expanded.
   */
  optionsMenuVisible: boolean

  /**
   * The error message displayed when it is unable to download the file.
   */
  errorMessage: string

  protected _value: File

  protected readonly STARTED_EVENT_TYPE: string = 'downloadstart'
  protected readonly COMPLETED_EVENT_TYPE: string = 'downloadcomplete'
  protected readonly FAILED_EVENT_TYPE: string = 'downloadfailure'

  protected readonly FINAL_STATE_DURATION = 2375

  protected _renderer2 = inject(Renderer2)

  @ViewChild('optionsMenu')
  private readonly _optionsMenuRef!: ElementRef<HTMLDivElement>

  @ViewChildren('optionsMenuItem')
  private readonly _optionsMenuItemRefs!: QueryList<ElementRef<HTMLButtonElement>>

  private _disposition: DownloadContentDisposition
  private _showOptions: boolean

  /**
   * Lifecycle hook for when the download button is clicked
   *
   * Request that a download begins.
   */
  onActionButtonClick(disposition: DownloadContentDisposition = this.disposition): void {
    this.toggleOptionsMenuVisibility(false)
    this.requestStart({ disposition })
  }

  /**
   * Lifecycle hook for when the options button is clicked.
   *
   * It will open the options menu.
   */
  onOptionsButtonClick(): void {
    this.toggleOptionsMenuVisibility(true)
  }

  /**
   * Lifecycle hook for when the options button is unfocused.
   *
   * It will close the options menu if the element that took focus is not either the menu itself or one of it's children.
   *
   * @param event The blur event that is emitted when the focus of the button is lost
   */
  onOptionsButtonBlur(event: FocusEvent): void {
    // If the element that caused the blur is in the options menu, return
    let isOptionsMenu = event.relatedTarget == this._optionsMenuRef.nativeElement
    let isOptionsMenuItem = this._optionsMenuItemRefs.some(it => it.nativeElement == event.relatedTarget)

    if (isOptionsMenu || isOptionsMenuItem) {
      return
    }

    // Close the options menu
    this.toggleOptionsMenuVisibility(false)
  }

  /**
   * Lifecycle hook for when the download request is completed
   *
   * Detect the changes made to the anchor element and save the file
   *
   * @param value   The file that has been downloaded
   * @param context The context in which the download was executed.
   */
  onActionComplete(value: File, context?: DownloadOptions): void {
    // Set up the data for the events
    let eventData: DownloadCompletedEventData = { file: value }

    if (context) {
      eventData.method = context?.method ?? this.method
      eventData.url = context?.url ?? this.url
      eventData.encoding = context?.encoding ?? this.formenctype
      eventData.params = context?.params ?? this._params
      eventData.disposition = context?.disposition ?? this.disposition
    }

    // Emit the event via Angular output and native DOM event
    this.completed.emit(eventData)
    let continued = this.dispatchEvent(this.COMPLETED_EVENT_TYPE, eventData)

    // If the native event was not cancelled, perform the default behaviour
    if (continued) {
      // Set the state to complete
      this.state = AsyncActionState.COMPLETE

      // Either open or save the file dependening on the disposition
      if (context.disposition === 'attachment') {
        this.saveFile(value)
      }

      else {
        this.openFile(value)
      }

      // Automatically transition to the idle state
      this.scheduleReturnToIdle()
    }
  }

  /**
   * Lifecycle hook for when the download request encountered an error
   *
   * Clear out any data of the file in memory and set the state of the button to `AsyncActionState.ERROR`
   *
   * @param error   The error thrown while trying to download the file.
   * @param context The context in which the download was executed.
   */
  onActionError(error: Error, context?: DownloadOptions): void {
    // Set up the data for the events
    let eventData: DownloadFailedEventData = { error }

    if (context) {
      eventData.method = context?.method ?? this.method
      eventData.url = context?.url ?? this.url
      eventData.encoding = context?.encoding ?? this.formenctype
      eventData.params = context?.params ?? this._params
      eventData.disposition = context?.disposition ?? this.disposition
    }

    // Emit the event via Angular output and native DOM event
    this.failed.emit(eventData)
    let continued = this.dispatchEvent(this.FAILED_EVENT_TYPE, eventData)

    // If the native event was not cancelled, perform the default behaviour
    if (continued) {
      // Set the state to error
      this.errorMessage = 'Unable to download file. Press the button to try again.'
      this.state = AsyncActionState.ERROR

      // Reset the value of the element
      this._value = null

      // Automatically transition to the idle state
      this.scheduleReturnToIdle()
    }
  }

  /**
   * Toggle whether or not the options menu is visible.
   *
   * If a specific value is provided it will set the options menu to that visibility state.
   *
   * @param value The specific state to set the menu to.
   */
  toggleOptionsMenuVisibility(value = !this.optionsMenuVisible): void {
    this.optionsMenuVisible = value
    this._changeDetectorRef.detectChanges()
  }

  /**
   * Request that the download of a file be started.
   *
   * This will emit a {@link DownloadEvent} of type `downloadstart`, if you wish to start a download without
   * dispatching any events please use the {@link DownloadComponent.download} method instead.
   *
   * @param args Configure the source, method, disposition of the file that is to be downloaded.
   *
   * @returns The downloaded file.
   */
  @Input()
  @HostBinding()
  async requestStart(args?: DownloadOptions): Promise<File> {
    // If a request is currently active, exit
    if (this.state === AsyncActionState.ACTIVE) {
      return null
    }

    let disposition = args?.disposition ?? this.disposition

    // Set up the data for the events
    let eventData: DownloadEventData = { disposition }

    // If the args do not contain a promise
    if (!(args?.fromPromise instanceof Promise)) {
      eventData.method = args?.method ?? this.method
      eventData.url = args?.url ?? this.url
      eventData.encoding = args?.encoding ?? this.formenctype
      eventData.params = args?.params ?? this._params
      eventData.disposition = disposition
    }

    // Emit the event via Angular output and native DOM event
    this.started.emit(eventData)
    let continued = this.dispatchEvent(this.STARTED_EVENT_TYPE, eventData)

    // If the native event was not cancelled, perform the default behaviour
    if (continued) {
      return await this.start(args)
    }

    return null
  }

  /**
   * Download a file from the provided source.
   *
   * @param args Configure the source, method, disposition of the file that is to be downloaded.
   *
   * @returns The downloaded file
   */
  @Input()
  @HostBinding()
  async start(args?: DownloadOptions): Promise<File> {
    // Toggle the active state
    this.state = AsyncActionState.ACTIVE

    let disposition = args?.disposition ?? this.disposition

    // Get the context
    let context = !(args?.fromPromise instanceof Promise) ? args : { disposition }

    // Get the asynchronous request
    let request = args?.fromPromise instanceof Promise ? args?.fromPromise : this.sendHttpRequest(args)

    // Await the response
    try {
      let response = await request
      this.onActionComplete(response, context)
      return response
    }

    // Catch any errors thrown while while making the HTTP request
    catch (e) {
      this.onActionError(e, context)
      return null
    }
  }

  /**
   * Determine the name of the file from the headers of the response
   *
   * Through the use of the `Content-Disposition` header in the response we can determine
   * how the file is to be downloaded and what it's file name is.
   *
   * @param response  The response of the download request
   *
   * @returns         The name of the file from the source
   */
  @Input()
  @HostBinding()
  getFileNameFromResponse(response: HttpResponse<unknown>): string {
    let fileNamePattern = /filename="(.*)"/
    let contentDisposition = response.headers.get('Content-Disposition')

    if (fileNamePattern.test(contentDisposition)) {
      return fileNamePattern.exec(contentDisposition)[1]
    }

    return this.fileName
  }

  /**
   * Create an HTTP request that will be used to download a file.
   *
   * @param args  The arguments used to create the request. If any of the options are missing,
   *              then the matching values of the element will be used as a default.
   *
   * @returns     HTTP request that can be sent via Angular's {@link HttpClient}.
   */
  @Input()
  @HostBinding()
  createHttpRequest(args: DownloadOptions): HttpRequest<unknown> {
    return super.createHttpRequest(args).clone({ responseType: 'blob' }) as HttpRequest<unknown>
  }

  /**
   * Send an HTTP request for the download.
   *
   * @param args  The arguments that will be used to populate an HTTP request.
   *
   * @returns     An asynchronous response from the requested URL.
   */
  protected async sendHttpRequest(args: DownloadOptions): Promise<File> {
    let request = this.createHttpRequest(args)

    return this._http
      .request(request)
      .pipe(
        filter(it => it instanceof HttpResponse),
        map((it: HttpResponse<Blob>) => {
          let fileName = this.getFileNameFromResponse(it)
          return new File([ it.body ], fileName, { type: it.body.type })
        })
      )
      .toPromise()
  }

  /**
   * Dispatch a native DOM event of the provided type and with the provided data.
   *
   * @param type  The type of event emitted.
   * @param data  The initial data for the event.
   *
   * @returns     `TRUE`, else it will return `FALSE` if the event's default behaviour is prevented.
   */
  protected dispatchEvent(type: string, data: DownloadEventData | DownloadCompletedEventData | DownloadFailedEventData): boolean {
    let event = new DownloadEvent(type, {
      bubbles: true,
      cancelable: true,
      ...data
    })
    return this._elementRef.nativeElement.dispatchEvent(event)
  }

  /**
   * Schedule the state to return to the idle state after a duration has passed.
   *
   * @param duration  The length of time in miliseconds until the state returns to {@link AsyncActionState.IDLE}.
   */
  private scheduleReturnToIdle(duration = this.FINAL_STATE_DURATION): void {
    setTimeout(() => this.state = AsyncActionState.IDLE, duration)
  }

  /**
   * Open the downloaded file in a new tab.
   *
   * The target that is opened is reused if another file is downloaded, as to not spam the user with countless blank targets.
   *
   * @param file  The file that will be opened
   */
  private openFile(file: File): void {
    let target = `gpei-download-${ file.name.replace(/\W/, '-') }`

    let url = URL.createObjectURL(file)

    this._document.defaultView.open(url, target)
  }

  /**
   * Save the downloaded file to the host machine.
   *
   * @param file  The file that will be saved
   */
  private saveFile(file: File): void {
    // Get the object URL for the file
    let url = URL.createObjectURL(file)

    // Create an anchor element for the URL and click it
    let element = this._renderer2.createElement('a')
    this._renderer2.setAttribute(element, 'href', url)
    this._renderer2.setAttribute(element, 'download', file.name)

    element.click()
  }

  /**
   * The response of the most recently run download.
   *
   * If a download fails to execute the value will be reset to `NULL`.
   */
  @HostBinding()
  get value(): File {
    return this._value
  }

  /**
   * Determine whether or not the downloaded file is opened in a new tab or is downloaded
   */
  @Input()
  @HostBinding()
  set disposition(value: DownloadContentDisposition) {
    let pendingValue = value

    if (pendingValue !== 'inline' && pendingValue !== 'attachment') {
      pendingValue = 'attachment'
    }

    this._disposition = pendingValue
  }

  get disposition(): DownloadContentDisposition {
    return this._disposition ?? 'attachment'
  }

  /**
   * Determine whether or not the other disposition options are available in a submenu
   */
  @Input()
  @HostBinding()
  set showOptions(value: string | boolean) {
    let pendingValue = value

    if (typeof pendingValue === 'string') {
      pendingValue = pendingValue.toLowerCase() !== 'false'
    }

    this._showOptions = pendingValue
  }

  get showOptions(): boolean {
    return this._showOptions
  }

  /**
   * An alias for the {@link DownloadComponent.url} property.
   *
   * @Deprecated  This should be avoided and any use should be replaced in favor
   *              of the {@link DownloadComponent.url} property.
   */
  @Input()
  @HostBinding()
  set href(value: string) {
    this.url = value
  }

  get href(): string {
    return this.url
  }
}

/**
 * @public
 */
export const registerDownloadElement = registerCustomElementFactory(DownloadComponent)
